/**
 * The class strings every tool widget is drawn with.
 *
 * The first 250 widgets each restate these inline — the same input, the same two
 * buttons, the same stat card — and they agree because they were copied from one
 * another. Family-built widgets import them instead, so five hundred tools keep one
 * look and a future restyle is one edit rather than five hundred.
 *
 * Plain strings, not components, so a widget that needs one extra class can append it
 * without a prop for every variation.
 */

/** A field's label, or a small section caption above a control group. */
export const LABEL = "font-medium text-foreground text-sm";

/** Secondary text under a field or beside a count. */
export const HINT = "text-muted-foreground text-xs";

/** A single-line text or number input. */
export const INPUT =
  "w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

/** The same input for codes, numbers, identifiers and data. */
export const INPUT_MONO = `${INPUT} font-mono`;

/** A multi-line text area for pasted text or data. */
export const TEXTAREA =
  "min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

/** A text area for prose rather than data — same box, proportional type. */
export const TEXTAREA_PROSE =
  "min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

/** A native `<select>`. */
export const SELECT =
  "w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary";

/** The page's main action. One per widget, usually. */
export const BUTTON_PRIMARY =
  "rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40";

/** Copy, clear, download, add-row and every other secondary action. */
export const BUTTON_SECONDARY =
  "rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40";

/** A compact secondary action inside a row or a card. */
export const BUTTON_SMALL =
  "rounded-md border border-border bg-card px-3 py-1 font-medium text-foreground text-xs transition-colors hover:bg-muted disabled:opacity-40";

/** One option in a row of radio "pills" (mode switches, style pickers). */
export const PILL =
  "flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5";

/** A bordered panel around a result, a group of fields or a preview. */
export const CARD = "rounded-lg border border-border bg-card p-4 sm:p-5";

/** A panel that holds the answer — subtly tinted so the eye lands on it. */
export const RESULT_CARD =
  "rounded-lg border border-primary/30 bg-primary/5 p-4 sm:p-5";

/** "Results" and other headings inside a widget. The page's own H2s are prose. */
export const WIDGET_H2 = "font-semibold text-foreground text-xl";

export const WIDGET_H3 = "font-semibold text-foreground text-lg";

/** A friendly validation message: what to enter, not what went wrong. */
export const NOTICE =
  "rounded-lg border border-border bg-muted/40 p-4 text-muted-foreground text-sm";

/** A real error: a failed lookup or input the tool cannot read. */
export const ERROR_NOTICE =
  "rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm";

/** A data table inside a widget. */
export const TABLE = "w-full border-collapse text-left text-sm";

/*
 * `whitespace-nowrap` and `[overflow-wrap:normal]` because the page body sets
 * `overflow-wrap: anywhere`, which on a phone broke headers mid-word ("FREQ/UEN/CY").
 * Tables already scroll sideways inside their own container, so a header never needs to
 * wrap to fit.
 */
export const TH =
  "whitespace-nowrap border-border border-b px-3 py-2 font-medium text-foreground text-xs uppercase tracking-wide [overflow-wrap:normal]";

export const TD = "border-border border-b px-3 py-2 text-foreground";
